import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import ProtocolsPage from '@/pages/ProtocolsPage';
import ProtocolDetailPage from '@/pages/ProtocolDetailPage';
import ManifestPage from '@/pages/ManifestPage';
import RecommendationsPage from '@/pages/RecommendationsPage';
import RecommendationDetailPage from '@/pages/RecommendationDetailPage';
import RecommendationAnalyticsPage from '@/pages/RecommendationAnalyticsPage';
import CdcExtractionsPage from '@/pages/CdcExtractionsPage';
import CdcExtractionDetailPage from '@/pages/CdcExtractionDetailPage';
import BlogPostsPage from '@/pages/BlogPostsPage';
import BlogPostEditorPage from '@/pages/BlogPostEditorPage';
import UsersPage from '@/pages/UsersPage';
import WebAnalyticsPage from '@/pages/WebAnalyticsPage';
import QrCodesPage from '@/pages/QrCodesPage';
import FunnelAnalyticsPage from '@/pages/FunnelAnalyticsPage';
import SessionsPage from '@/pages/SessionsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/protocols" replace />} />
        <Route path="protocols" element={<ProtocolsPage />} />
        <Route path="protocols/:id" element={<ProtocolDetailPage />} />
        <Route path="cdc-extractions" element={<CdcExtractionsPage />} />
        <Route path="cdc-extractions/:id" element={<CdcExtractionDetailPage />} />
        <Route path="manifest" element={<ManifestPage />} />
        <Route path="recommendations" element={<RecommendationsPage />} />
        <Route path="recommendations/:id" element={<RecommendationDetailPage />} />
        <Route path="analytics/recommendations" element={<RecommendationAnalyticsPage />} />
        <Route path="analytics/web" element={<WebAnalyticsPage />} />
        <Route path="qr-codes" element={<QrCodesPage />} />
        {/* The Google Analytics page was removed; keep old bookmarks working. */}
        <Route path="analytics/google" element={<Navigate to="/analytics/web" replace />} />
        <Route path="analytics/funnel" element={<FunnelAnalyticsPage />} />
        <Route path="analytics/sessions" element={<SessionsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="blog" element={<BlogPostsPage />} />
        <Route path="blog/new" element={<BlogPostEditorPage />} />
        <Route path="blog/:id" element={<BlogPostEditorPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
